import React, { useState, useEffect, useContext, useRef } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { notifySuccess, notifyError } from '../utils/toast';

interface EventoProps 
{
  event?: 
  {
    id: number;
    name: string;
    autonext: boolean;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    codereadr_service_id: string | null;
    codereadr_database_id: string | null;
  };

  id_client: number;

  onSave: (eventData: 
  {
    id?: number;
    name: string;
    autonext: boolean;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    codereadr_service_id: string | null;
    codereadr_database_id: string | null;
  }) => void;

  onDelete?: (eventId: number) => void;

  onCancel: () => void;
}

const Evento: React.FC<EventoProps> = ({ event, id_client, onSave, onDelete, onCancel }) => 
{
  const { token } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [autonext, setAutonext] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [codereadrServiceId, setCodereadrServiceId] = useState<string | null>(null);
  const [codereadrDatabaseId, setCodereadrDatabaseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal

  // Determinar si estamos en modo de edición o creación
  const isEditing = event && event.id !== -1;
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
  const moreActionsRef = useRef<HTMLDivElement>(null);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    if (event) 
    {
      setName(event.name);
      setAutonext(event.autonext);
      setStartDate(event.start_date);
      setEndDate(event.end_date);
      setStartTime(event.start_time);
      setEndTime(event.end_time);
      setCodereadrServiceId(event.codereadr_service_id);
      setCodereadrDatabaseId(event.codereadr_database_id);
    } 
    else 
    {
      // Inicializa el estado para un nuevo evento
      setName('');
      setAutonext(false);
      setStartDate('');
      setEndDate('');
      setStartTime('');
      setEndTime('');
      setCodereadrServiceId('');
      setCodereadrDatabaseId('');
    }
  }, [event]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    // Función para manejar clics fuera del componente
    const handleClickOutside = (event: MouseEvent) => 
    {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Función para manejar presiones de teclas
    const handleEscPress = (event: KeyboardEvent) => 
    {
      if (event.key === 'Escape') 
      {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);

    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [onCancel]);  

  //-----------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => 
  {
    e.preventDefault();
    try 
    {
      const formData = new URLSearchParams();
      formData.append('event_name', name);
      formData.append('autonext', autonext ? 'true' : 'false');
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('start_time', startTime);
      formData.append('end_time', endTime);
      formData.append('codereadr_service_id', codereadrServiceId ?? '');
      formData.append('codereadr_database_id', codereadrDatabaseId ?? '');
      formData.append('id_client', id_client.toString());

      const url = isEditing ? 'web/update_event' : 'web/create_event';

      if (event && event.id !== null)
        formData.append('id', event.id.toString());

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'},
        body: formData.toString(),
      });

      if (response.ok) 
      {
        onSave({ 
          id: event?.id ?? undefined,
          name, 
          autonext, 
          start_date: startDate, 
          end_date: endDate, 
          start_time: startTime, 
          end_time: endTime,
          codereadr_service_id: codereadrServiceId,
          codereadr_database_id: codereadrDatabaseId});
        notifySuccess('Evento guardado correctamente');
      } 
      else 
      {
        const errorData = await response.json();
        setError(errorData.message || 'Error al guardar el evento.');
      }
    } 
    catch (error) 
    {
      setError('Error de red. Por favor, intente nuevamente.');
    }
  };

  //-----------------------------------------------------------

  const handleDuplicar = async () => 
  {
    if (event && event.id !== null && event.id !== -1 && onDelete) 
      {
        try 
        {
          const formData = new URLSearchParams();
          formData.append('id', event.id.toString());
  
          const response = await apiFetch(`web/duplicate_event`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'},
            body: formData.toString()});
  
          if (response.ok) 
          {
            onCancel();
            notifySuccess('Evento duplicado correctamente');
          } 
          else 
          {
            const errorData = await response.json();
            notifyError('Error al duplicar el evento', errorData.error || 'Error al duplicar el evento.');
            onCancel();
          }
        } 
        catch (error) 
        {
          setError('Error de red. Por favor, intente nuevamente.');
        }
      }
  };

  const handleExportCodereadr = async () => 
  {
    if (event && event.id !== null && onDelete) 
      {
        try 
        {
          const queryParams = new URLSearchParams({
            id_event: event.id.toString(),
          });

          const response = await apiFetch(`web/create_event_codereadr?${queryParams.toString()}`, {
              method: 'GET'
          });

          if (response.ok)
          {
            notifySuccess('Evento exportado correctamente');
            onCancel();
          }
          else
          {
            const errorData = await response.json();
            setError(errorData.error || 'Error al exportar el evento.');
          }
        } 
        catch (error) 
        {
          setError('Error de red. Por favor, intente nuevamente.');
        }
      }
  };

  const handleDesynchronizeCodereadr = async () => 
  {
    if (event && event.id !== null && onDelete) 
      {
        try 
        {
          const queryParams = new URLSearchParams({
            id_event: event.id.toString(),
          });

          const response = await apiFetch(`web/desync_event_codereadr?${queryParams.toString()}`, {
            method: 'GET'
          });

          if (response.ok)
          {
            notifySuccess('Evento desincronizado correctamente');
            onCancel();
          }
          else
          {
            const errorData = await response.json();
            setError(errorData.error || 'Error al desincronizar el evento.');
          }
        } 
        catch (error) 
        {
          setError('Error de red. Por favor, intente nuevamente.');
        }
      }
  };

  //-----------------------------------------------------------

  const handleDelete = () => 
  {
    if (event && event.id !== null && event.id !== -1)
      setIsModalOpen(true);
  };

  //-----------------------------------------------------------

  const handleConfirmDelete = async () => 
  {
    if (event && event.id !== null && event.id !== -1 && onDelete) 
    {
      try 
      {
        const formData = new URLSearchParams();
        formData.append('id', event.id.toString());

        const response = await apiFetch(`web/delete_event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'},
          body: formData.toString()});

        if (response.ok) 
        {
          onDelete(event.id);
          setIsModalOpen(false);
          notifySuccess('Evento eliminado correctamente');
        } 
        else 
        {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el evento.');
          notifyError('Error al eliminar el evento', errorData.error || 'Error al eliminar el evento.');
        }
      } 
      catch (error) 
      {
        setError('Error de red. Por favor, intente nuevamente.');
      }
    }
  };

  //-----------------------------------------------------------

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreActionsRef.current && !moreActionsRef.current.contains(e.target as Node)) {
        setIsMoreActionsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  //-----------------------------------------------------------
  
  return (
    <div className="evento-container" ref={modalRef}>
      <button type="button" className="modal-close-button" onClick={onCancel} aria-label="Cerrar">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
      <h2>{isEditing ? 'Editar Evento' : 'Crear Nuevo Evento'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre del evento"
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <input
              id="start_date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Fecha de inicio"
              required
            />
          </div>
          <div className="form-group">
            <input
              id="end_date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="Fecha de fin"
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <input
              id="start_time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              title="Hora de inicio"
              required
            />
          </div>
          <div className="form-group">
            <input
              id="end_time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              title="Hora de fin"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={autonext}
              onChange={(e) => setAutonext(e.target.checked)}
            />
            Autonext
          </label>
        </div>
        <div className="form-row">
          <div className="form-group">
            <input
              id="codereadr_service_id"
              type="text"
              value={codereadrServiceId ?? ''}
              onChange={(e) => setCodereadrServiceId(e.target.value || null)}
              title="ID del service de Codereadr"
              placeholder="ID del service de Codereadr" 
            />
          </div>
          <div className="form-group">
            <input
              id="codereadr_database_id"
              type="text"
              value={codereadrDatabaseId ?? ''}
              onChange={(e) => setCodereadrDatabaseId(e.target.value || null)}
              title="ID del database de Codereadr"
              placeholder="ID del database de Codereadr"
            />
          </div>
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-actions">          
          {isEditing && event && event.id !== null && event.id !== -1 && (
            <button type="button" className="delete-event-button" onClick={handleDelete}>
              <i className="fas fa-trash"></i>Borrar
            </button>
          )}
          <div className="form-actions-right">
            {isEditing && event && event.id !== null && event.id !== -1 && (
              <div className="more-actions-menu" ref={moreActionsRef}>
                <button
                  type="button"
                  className="more-actions-trigger"
                  onClick={() => setIsMoreActionsOpen(!isMoreActionsOpen)}
                >
                  Más acciones
                </button>

                {isMoreActionsOpen && (
                  <div className="more-actions-dropdown">
                    <button
                      type="button"
                      className="more-actions-item"
                      onClick={() => { setIsMoreActionsOpen(false); handleDuplicar(); }}
                    >
                      <i className="fas fa-copy"></i> Duplicar
                    </button>
                    <button
                      type="button"
                      className="more-actions-item"
                      onClick={() => { setIsMoreActionsOpen(false); handleExportCodereadr(); }}
                    >
                      <i className="fas fa-upload"></i> Exportar a CodeREADr
                    </button>
                    <button
                      type="button"
                      className="more-actions-item"
                      onClick={() => { setIsMoreActionsOpen(false); handleDesynchronizeCodereadr(); }}
                    >
                      <i className="fa-solid fa-arrow-rotate-left"></i> Desincronizar de CodeREADr
                    </button>
                  </div>
                )}
              </div>
            )}
            <button type="submit" className='save-button'>{isEditing ? 'Guardar' : 'Crear'}</button>
          </div>
        </div>
      </form>
      {isModalOpen && (
        <ModalEliminacion
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
        >
          <p>¿Estás seguro de que quieres eliminar este evento?</p>
        </ModalEliminacion>
      )}
    </div>
  );
};

export default Evento;
